import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Zap, Upload, Trash2, Loader2, CalendarDays, TrendingUp, TrendingDown, Info,
  BarChart3, PieChart as PieIcon, Lightbulb, LineChart as LineIcon, ArrowLeftRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PowerTable } from "@/components/power/PowerShared";
import MonthComparison from "@/components/power/MonthComparison";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, Cell, LabelList, PieChart, Pie,
} from "recharts";

const TSS_META = [
  { key: "TSS 1", color: "#2563eb", iconBg: "bg-blue-100", iconColor: "text-blue-600", border: "border-l-blue-500", tint: "from-blue-50/80" },
  { key: "TSS 2", color: "#16a34a", iconBg: "bg-green-100", iconColor: "text-green-600", border: "border-l-green-500", tint: "from-green-50/80" },
  { key: "TSS 3", color: "#7c3aed", iconBg: "bg-purple-100", iconColor: "text-purple-600", border: "border-l-purple-500", tint: "from-purple-50/80" },
  { key: "TSS 4", color: "#0d9488", iconBg: "bg-teal-100", iconColor: "text-teal-600", border: "border-l-teal-500", tint: "from-teal-50/80" },
];
const COLOR_OF = Object.fromEntries(TSS_META.map((m) => [m.key, m.color]));

const ID_MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

const PERIODS = [
  { value: "monthly", label: "Bulanan" },
  { value: "daily", label: "Harian" },
  { value: "weekly", label: "Mingguan" },
];

/* ---------- helpers ---------- */
const fmtNum = (v, dec = 0) =>
  v == null ? "—" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: dec });

function fmtEnergy(kwh) {
  if (kwh == null) return { v: "—", u: "" };
  const abs = Math.abs(kwh);
  if (abs >= 1e6) return { v: (kwh / 1e6).toLocaleString("id-ID", { maximumFractionDigits: 2 }), u: "GWh" };
  if (abs >= 1e3) return { v: (kwh / 1e3).toLocaleString("id-ID", { maximumFractionDigits: 2 }), u: "MWh" };
  return { v: kwh.toLocaleString("id-ID", { maximumFractionDigits: 0 }), u: "kWh" };
}

const fmtDateID = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${ID_MONTHS[m - 1]} ${y}`;
};

function isoWeekLabel(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const isoYear = d.getUTCFullYear();
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const week = 1 + Math.round(((d - jan4) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return `${ID_MONTHS[m - 1]} ${y}`;
};

function aggregate(items, period, keys) {
  if (period === "daily") {
    return items.map((it) => ({ period: it.date, ...Object.fromEntries(keys.map((k) => [k, it.values?.[k] ?? null])) }));
  }
  const buckets = new Map();
  for (const it of items) {
    const label = period === "weekly" ? isoWeekLabel(it.date) : monthLabel(it.date.slice(0, 7));
    const sortKey = period === "weekly" ? isoWeekLabel(it.date) : it.date.slice(0, 7);
    if (!buckets.has(sortKey)) buckets.set(sortKey, { period: label, __sort: sortKey });
    const b = buckets.get(sortKey);
    for (const k of keys) {
      const v = it.values?.[k];
      if (v != null) b[k] = (b[k] || 0) + v;
    }
  }
  return [...buckets.values()].sort((a, b) => (a.__sort < b.__sort ? -1 : 1));
}

const shiftYear = (iso, n) => {
  const [y, m, d] = iso.split("-");
  return `${Number(y) + n}-${m}-${d}`;
};

/* ---------- small components ---------- */
function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const sorted = [...payload].filter((p) => p.value != null).sort((a, b) => b.value - a.value);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-3 text-xs min-w-[180px]">
      <div className="font-bold text-slate-800 mb-2">{label}</div>
      {sorted.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-6 py-1">
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            {p.dataKey}
          </span>
          <span className="font-semibold tabular-nums text-slate-800">{fmtNum(p.value)} kWh</span>
        </div>
      ))}
    </div>
  );
}

function KpiCard({ meta, total, delta, rangeLabel }) {
  const e = fmtEnergy(total);
  return (
    <div
      data-testid={`tss-kpi-${meta.key.replace(/\s+/g, "-")}`}
      className={`bg-gradient-to-br ${meta.tint} to-white rounded-xl border border-slate-200/60 border-l-4 ${meta.border} p-5 kpi-shadow`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-full ${meta.iconBg} ${meta.iconColor} flex items-center justify-center`}>
          <Zap className="w-4.5 h-4.5 w-4 h-4" />
        </div>
        <div className="font-bold text-slate-800 text-sm">{meta.key}</div>
      </div>
      <div className="text-3xl font-black text-slate-900 tabular-nums">
        {e.v} <span className="text-base font-bold text-slate-500">{e.u}</span>
      </div>
      <div className="text-[11px] text-slate-400 mt-1">Total Konsumsi {rangeLabel ? `(${rangeLabel})` : ""}</div>
      {delta != null && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-semibold ${delta <= 0 ? "text-green-600" : "text-red-600"}`}>
          {delta <= 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
          {Math.abs(delta).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%
          <span className="text-slate-400 font-medium">vs. tahun lalu</span>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, title, subtitle, actions, children, className = "", testId }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200/60 kpi-shadow p-6 ${className}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          {Icon && (
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#1e40af] flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4" />
            </div>
          )}
          <div>
            <div className="font-bold text-slate-800 text-sm">{title}</div>
            {subtitle && <div className="text-[11px] text-slate-400 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

/* ---------- page ---------- */
export default function PowerTSS() {
  const { isAdmin } = useAuth();
  const [allItems, setAllItems] = useState([]);
  const [keys, setKeys] = useState(TSS_META.map((m) => m.key));
  const [period, setPeriod] = useState("monthly");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/power/tss", { params: { period: "daily" } });
      const items = res.data.items || [];
      setAllItems(items);
      setKeys(res.data.keys?.length ? res.data.keys : TSS_META.map((m) => m.key));
      if (items.length) {
        setRangeStart((s) => s || items[0].date);
        setRangeEnd((e) => e || items[items.length - 1].date);
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // choose sensible default period: monthly needs >= 2 months
  useEffect(() => {
    if (!allItems.length) return;
    const months = new Set(allItems.map((it) => it.date.slice(0, 7)));
    if (months.size < 2) setPeriod((p) => (p === "monthly" ? "daily" : p));
  }, [allItems]);

  const items = useMemo(
    () => allItems.filter((it) => (!rangeStart || it.date >= rangeStart) && (!rangeEnd || it.date <= rangeEnd)),
    [allItems, rangeStart, rangeEnd]
  );

  const series = useMemo(() => aggregate(items, period, keys), [items, period, keys]);

  const totals = useMemo(() => {
    const t = {};
    for (const k of keys) t[k] = items.reduce((s, it) => s + (it.values?.[k] ?? 0), 0);
    return t;
  }, [items, keys]);
  const grandTotal = keys.reduce((s, k) => s + (totals[k] || 0), 0);

  // vs previous year (same window shifted -1 year) — hidden when no data
  const prevDeltas = useMemo(() => {
    if (!rangeStart || !rangeEnd) return {};
    const ps = shiftYear(rangeStart, -1);
    const pe = shiftYear(rangeEnd, -1);
    const prev = allItems.filter((it) => it.date >= ps && it.date <= pe);
    if (!prev.length) return {};
    const out = {};
    for (const k of keys) {
      const pv = prev.reduce((s, it) => s + (it.values?.[k] ?? 0), 0);
      if (pv > 0) out[k] = ((totals[k] - pv) / pv) * 100;
    }
    return out;
  }, [allItems, rangeStart, rangeEnd, keys, totals]);

  const rangeLabel = rangeStart && rangeEnd ? `${fmtDateID(rangeStart)} – ${fmtDateID(rangeEnd)}` : "";
  const lastUpdated = useMemo(() => {
    const ts = allItems.map((it) => it.uploaded_at).filter(Boolean).sort();
    return ts.length ? new Date(ts[ts.length - 1]).toLocaleString("id-ID") : "—";
  }, [allItems]);

  const barData = keys.map((k) => ({ name: k, total: totals[k] || 0 }));
  const pieData = keys
    .map((k) => ({ name: k, value: totals[k] || 0 }))
    .filter((d) => d.value > 0);

  const insights = useMemo(() => {
    if (!series.length || !grandTotal) return [];
    const out = [];
    const top = keys.reduce((a, b) => ((totals[a] || 0) >= (totals[b] || 0) ? a : b));
    let peak = null;
    for (const row of series) if (row[top] != null && (!peak || row[top] > peak[top])) peak = row;
    const eTop = fmtEnergy(totals[top]);
    out.push({
      icon: TrendingUp, color: "text-purple-600",
      title: top,
      text: `Memiliki konsumsi tertinggi (${eTop.v} ${eTop.u})${peak ? ` dengan puncak di ${peak.period}` : ""}.`,
    });
    if (series.length >= 2) {
      const first = series[0];
      const last = series[series.length - 1];
      let worst = null;
      for (const k of keys) {
        if (first[k] > 0 && last[k] != null) {
          const pct = ((last[k] - first[k]) / first[k]) * 100;
          if (!worst || pct < worst.pct) worst = { k, pct };
        }
      }
      if (worst) {
        out.push({
          icon: worst.pct <= 0 ? TrendingDown : TrendingUp,
          color: worst.pct <= 0 ? "text-green-600" : "text-red-600",
          title: worst.k,
          text: `${worst.pct <= 0 ? "Menunjukkan penurunan" : "Menunjukkan kenaikan"} konsumsi paling ${worst.pct <= 0 ? "signifikan" : "rendah"} (${worst.pct >= 0 ? "+" : "−"}${Math.abs(worst.pct).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%) dibanding awal periode.`,
        });
      }
      const fSum = keys.reduce((s, k) => s + (first[k] || 0), 0);
      const lSum = keys.reduce((s, k) => s + (last[k] || 0), 0);
      if (fSum > 0) {
        const pct = ((lSum - fSum) / fSum) * 100;
        out.push({
          icon: Info, color: "text-blue-600",
          title: "Secara keseluruhan",
          text: `Total konsumsi 4 TSS mengalami ${pct >= 0 ? "kenaikan" : "penurunan"} ${Math.abs(pct).toLocaleString("id-ID", { maximumFractionDigits: 1 })}% dibanding awal periode.`,
        });
      }
    }
    return out;
  }, [series, keys, totals, grandTotal]);

  /* ---------- actions ---------- */
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/power/upload", fd);
      toast.success(`Upload berhasil — TSS: ${res.data.tss_rows} baris, RSS: ${res.data.rss_rows} baris tersimpan`);
      setRangeStart(""); setRangeEnd("");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const deleteAll = async () => {
    if (!window.confirm("Hapus SEMUA data konsumsi TSS?")) return;
    try {
      const res = await api.delete("/power/tss");
      toast.success(`${res.data.deleted} data TSS dihapus`);
      setRangeStart(""); setRangeEnd("");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const deleteRow = async (date) => {
    if (!window.confirm(`Hapus data TSS tanggal ${date}?`)) return;
    try {
      await api.delete("/power/tss", { params: { date } });
      toast.success(`Data ${date} dihapus`);
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const gt = fmtEnergy(grandTotal);

  return (
    <div className="space-y-6 pb-8" data-testid="power-tss-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Operasional MRT Jakarta</div>
          <h1 className="text-3xl font-black text-slate-900 mt-1">Power Consumption – 4 TSS</h1>
          <p className="text-slate-500 text-sm mt-1">Tren konsumsi energi pada Traction Sub System (TSS)</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="tss-date-range-btn" className="h-9 text-xs font-semibold text-slate-700">
                  <CalendarDays className="w-4 h-4 mr-2 text-[#1e40af]" />
                  {rangeLabel || "Pilih periode"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-3 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rentang Tanggal</div>
                <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} data-testid="tss-range-start" className="h-9 text-xs" />
                <Input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} data-testid="tss-range-end" className="h-9 text-xs" />
              </PopoverContent>
            </Popover>
            <div className="text-right text-[11px] text-slate-400 leading-tight">
              <div>Terakhir diperbarui</div>
              <div className="font-semibold text-slate-600">{lastUpdated}</div>
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} data-testid="tss-file-input" />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="tss-upload-btn"
                className="h-9 bg-[#1e40af] hover:bg-[#1e3a8a] text-white text-xs font-semibold"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Excel
              </Button>
              <Button
                onClick={deleteAll}
                variant="outline"
                data-testid="tss-delete-all-btn"
                className="h-9 text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus Semua
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {TSS_META.filter((m) => keys.includes(m.key)).map((m) => (
          <KpiCard key={m.key} meta={m} total={totals[m.key]} delta={prevDeltas[m.key]} rangeLabel={rangeLabel} />
        ))}
      </div>

      {/* Trend chart */}
      <Card
        icon={LineIcon}
        title="Tren Power Consumption 4 TSS"
        subtitle={rangeLabel ? `Periode ${rangeLabel}` : "Belum ada data"}
        testId="tss-trend-card"
        actions={
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="tss-period-filter">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                data-testid={`tss-period-${p.value}`}
                className={`px-4 py-1.5 text-xs font-semibold transition-colors ${
                  period === p.value ? "bg-[#1e40af] text-white" : "bg-white text-slate-500 hover:bg-slate-50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        }
      >
        {series.length ? (
          <div className="h-[380px]" data-testid="tss-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 10, right: 20, bottom: 5, left: 18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} minTickGap={20} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => fmtNum(v)}
                  width={82}
                  label={{ value: "Power Consumption (kWh)", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#64748b", fontWeight: 600 } }}
                />
                <Tooltip content={<TrendTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {keys.map((k) => (
                  <Line key={k} type="monotone" dataKey={k} stroke={COLOR_OF[k] || "#334155"} strokeWidth={2.5} dot={{ r: 3, strokeWidth: 0, fill: COLOR_OF[k] }} activeDot={{ r: 5 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[380px] flex items-center justify-center text-sm text-slate-400" data-testid="tss-chart-empty">
            Belum ada data — upload logsheet Excel untuk melihat tren.
          </div>
        )}
      </Card>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card icon={BarChart3} title={`Total Konsumsi per TSS${rangeLabel ? ` (${rangeLabel})` : ""}`} testId="tss-bar-card">
          {grandTotal ? (
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 24, right: 8, bottom: 0, left: 8 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]} maxBarSize={56}>
                    {barData.map((d) => (
                      <Cell key={d.name} fill={COLOR_OF[d.name] || "#334155"} />
                    ))}
                    <LabelList
                      dataKey="total"
                      position="top"
                      formatter={(v) => { const e = fmtEnergy(v); return `${e.v} ${e.u}`; }}
                      style={{ fontSize: 11, fontWeight: 700, fill: "#334155" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">Belum ada data.</div>
          )}
        </Card>

        <Card icon={PieIcon} title="Kontribusi Konsumsi Energi" testId="tss-donut-card">
          {pieData.length ? (
            <div className="flex items-center gap-4 h-[240px]">
              <div className="relative w-1/2 h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={85} paddingAngle={2} strokeWidth={0}>
                      {pieData.map((d) => (
                        <Cell key={d.name} fill={COLOR_OF[d.name] || "#334155"} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-[10px] text-slate-400 font-semibold">Total</div>
                  <div className="text-lg font-black text-slate-800 tabular-nums">{gt.v} {gt.u}</div>
                </div>
              </div>
              <div className="flex-1 space-y-2">
                {[...pieData].sort((a, b) => b.value - a.value).map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-slate-600 font-medium">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOR_OF[d.name] }} />
                      {d.name}
                    </span>
                    <span className="font-bold text-slate-800 tabular-nums">
                      {((d.value / grandTotal) * 100).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">Belum ada data.</div>
          )}
        </Card>

        <Card icon={Lightbulb} title="Insight" testId="tss-insight-card">
          {insights.length ? (
            <div className="space-y-3">
              {insights.map((ins) => (
                <div key={ins.title + ins.text} className="flex gap-2.5 bg-slate-50/80 rounded-lg p-3">
                  <ins.icon className={`w-4 h-4 mt-0.5 shrink-0 ${ins.color}`} />
                  <div className="text-xs">
                    <div className={`font-bold ${ins.color}`}>{ins.title}</div>
                    <div className="text-slate-600 mt-0.5 leading-relaxed">{ins.text}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">Belum ada data.</div>
          )}
        </Card>
      </div>

      {/* Month comparison */}
      <Card icon={ArrowLeftRight} title="Perbandingan Antar Bulan" subtitle="Bandingkan konsumsi dua bulan pilihan" testId="tss-cmp-card">
        <MonthComparison items={allItems} keys={keys} entityLabel="TSS" testIdPrefix="tss" />
      </Card>

      {/* Data table */}
      <Card icon={CalendarDays} title={`Data Upload (${items.length} hari)`} testId="tss-table-card">
        <PowerTable items={items} keys={keys} isAdmin={isAdmin} onDeleteRow={deleteRow} testId="tss-table" />
      </Card>
    </div>
  );
}
